<template>
  <div class="sidebar-add-popover">
    <TirPmPopover
      ref="$popover"
      :position="position"
      :width="width"
      @close="onClosePopover"
      @open="onOpenPopover"
    >
      <slot name="target" />

      <template #content>
        <SidebarAddPopoverFolderForm
          v-if="viewFolderForm"
          :namespace-id="namespaceId"
          :parent-group-id="parentGroupId"
          :location="location"
          @on-create="onCreateGroup"
        />
        <FormCreateQueue
          v-else-if="viewQueueForm"
          :group-id="parentGroupId"
          :namespace-id="namespaceId"
          @on-create="onCreateQueue"
        />
        <PopoverOptionList v-else>
          <PopoverOptionLi
            v-if="[TreeItemType.Namespace, TreeItemType.Group].includes(location)"
            @click.stop="viewFolderForm = true"
          >
            <template #left-icon>
              <FolderIcon />
            </template>
            Добавить папку
          </PopoverOptionLi>
          <PopoverOptionLi
            v-if="location !== TreeItemType.Queue"
            @click.stop="viewQueueForm = true"
          >
            <template #left-icon>
              <ListBulletIcon />
            </template>
            Добавить очередь
          </PopoverOptionLi>
        </PopoverOptionList>
      </template>
    </TirPmPopover>
  </div>
</template>

<script lang="ts" setup>
import { computed, PropType, ref } from 'vue';
import { PopoverPositionEnum, TirPmPopover } from 'tir-components';
import { PopoverOptionList, PopoverOptionLi } from '../popover-option-list';
import { FolderIcon, ListBulletIcon } from 'tir-style-system/icons/outline';
import SidebarAddPopoverFolderForm from '../sidebar-add-popover-folder-form.vue';
import SidebarAddPopoverQueueForm from '../sidebar-add-popover-queue-form.vue';
import { TreeItemType } from '../../enums';
import { GroupModel, QueueTasksModel } from '../../models';
import { FormCreateQueue } from '@/components/form-create-queue';

const emit = defineEmits<{
  /**Событие при создании группы */
  (e: 'onCreateGroup', group: GroupModel): void;
  /**Событие при создании очереди задач */
  (e: 'onCreateQueue', queue: QueueTasksModel): void;
  /**Событие смены видимости поповера */
  (e: 'toggleVisible', value: boolean): void;
}>();

defineProps({
  /**Локация */
  location: {
    type: String as PropType<TreeItemType>,
    required: true,
  },
  /**ID неймспейса */
  namespaceId: {
    type: String,
    required: true,
  },
  /**ID родительской группы */
  parentGroupId: {
    type: Number,
  },
  position: {
    type: String as PropType<PopoverPositionEnum>,
    default: PopoverPositionEnum.Auto,
  },
});

/**Ссылка на компонент поповера */
const $popover = ref<InstanceType<typeof TirPmPopover>>();

/**Видимость формы создания папки */
const viewFolderForm = ref(false);
/**Видимость формы создания очереди */
const viewQueueForm = ref(false);

/**Ширина поповера */
const width = computed(() => {
  if (viewFolderForm.value || viewQueueForm.value) return '25rem';
  return '17.5rem';
});

/**
 * * При открытии поповера
 */
const onOpenPopover = () => {
  emit('toggleVisible', true);
};

/**
 * * При закрытии поповера
 */
const onClosePopover = () => {
  viewFolderForm.value = false;
  viewQueueForm.value = false;
  emit('toggleVisible', false);
};

/**
 * * При создании группы
 * @param group Группа
 */
const onCreateGroup = (group: GroupModel) => {
  emit('onCreateGroup', group);
  $popover.value?.close();
};

/**
 * * При создании очереди задач
 * @param queue Очередь задач
 */
const onCreateQueue = (queue: QueueTasksModel) => {
  emit('onCreateQueue', queue);
  $popover.value?.close();
};
</script>

<style lang="scss" scoped>
.sidebar-add-popover {
  display: flex;
}
</style>
